import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GitCompare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProjectShell, type Crumb } from "@/components/projects/project-shell";
import { VideoCard } from "@/components/projects/video-card";
import { useVideoVersions, type VideoVersion } from "@/hooks/use-videos";
import { useProject } from "@/hooks/use-projects";
import type { Video } from "@/lib/api-types";

/** Map a version row to the Video shape VideoCard expects. versionCount=1 so the
 *  card renders as a plain single asset (no stack badge, opens the player). */
function toVideo(v: VideoVersion, projectId: string): Video {
  return {
    id: v.id,
    title: v.title,
    projectId,
    assetType: v.assetType,
    cdnUrl: v.cdnUrl,
    approvalStatus: v.approvalStatus,
    fileSizeBytes: v.fileSizeBytes,
    durationSeconds: v.durationSeconds,
    uploaderName: v.uploaderName,
    createdAt: v.createdAt ?? undefined,
    commentCount: v.commentCount,
    versionNumber: v.versionNumber,
    versionCount: 1,
  };
}

export default function VersionsPage() {
  const { videoId = "" } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const prefix = loc.pathname.startsWith("/admin") ? "/admin" : "/app";

  const versionsQuery = useVideoVersions(videoId);
  const projectId = versionsQuery.data?.projectId ?? "";
  const { data: project } = useProject(projectId || undefined);

  // Newest first for display. Backend returns ASC by versionNumber.
  const versions = useMemo(
    () => [...(versionsQuery.data?.versions ?? [])].sort((a, b) => b.versionNumber - a.versionNumber),
    [versionsQuery.data]
  );
  const latestNumber = versions.length ? versions[0].versionNumber : 0;

  // Compare-select: at most two, FIFO when a third is picked.
  const [selected, setSelected] = useState<string[]>([]);
  function toggle(id: string) {
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 2 ? [cur[1], id] : [...cur, id]
    );
  }
  function compare() {
    if (selected.length !== 2) return;
    navigate(`${prefix}/compare/${selected[0]}/${selected[1]}`);
  }

  const title = versions[0]?.title ?? "Asset";
  const projectsBasePath = `${prefix}/projects`;

  // Breadcrumb for the shared top bar: All Projects / project / video title.
  const crumbs: Crumb[] = [
    { label: project?.name ?? "…", to: projectId ? `${projectsBasePath}/${projectId}` : undefined },
    { label: title },
  ];

  return (
    <ProjectShell
      projectId={projectId || undefined}
      projectsBasePath={projectsBasePath}
      crumbs={crumbs}
    >
      <div className="surface-panel flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-xl">
        {/* Toolbar — version count + compare controls */}
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 text-sm md:px-6 md:gap-4">
          <span className="text-muted-foreground">
            {versions.length} {versions.length === 1 ? "version" : "versions"}
          </span>

          <div className="flex-1" />

          <span className="hidden text-xs text-muted-foreground sm:inline">
            {selected.length === 0
              ? "Select two to compare"
              : selected.length === 1
                ? "Select one more to compare"
                : "Ready to compare"}
          </span>
          <Button onClick={compare} disabled={selected.length !== 2} size="sm">
            <GitCompare className="h-4 w-4" />
            Compare
          </Button>
        </div>

        {/* Scrolling content */}
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
          {versionsQuery.isLoading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : versionsQuery.isError ? (
            <div className="py-20 text-center text-sm text-destructive">Couldn't load versions.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {versions.map((v) => (
                <VideoCard
                  key={v.id}
                  projectId={projectId}
                  video={toVideo(v, projectId)}
                  view="grid"
                  watchPath={`${prefix}/watch`}
                  cardContext="version"
                  versionBadge={{ number: v.versionNumber, latest: v.versionNumber === latestNumber }}
                  selected={selected.includes(v.id)}
                  onToggleSelected={() => toggle(v.id)}
                  onSelectExclusive={() => toggle(v.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ProjectShell>
  );
}
